import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { PrayerOverview, PrayerRequest } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { dayTitle, printUrl, whatsappText, whatsappUrl } from './share';
import { formatWhen } from '../events/shared';
import { PrayerChainsCard } from './PrayerChains';
import { useTabParam } from '../../shared/ui/useTabParam';

type Tab = 'church' | 'mine' | 'pastoral' | 'team' | 'jornadas';

const TABS: Record<Tab, string> = { church: 'Iglesia', mine: 'Mías', pastoral: 'Pastoral', team: 'Ministerio de Oración', jornadas: 'Jornadas y cadenas' };
const STATUS: Record<PrayerRequest['status'], string> = { open: 'Abierta', answered: 'Respondida', closed: 'Cerrada' };
const when = (iso: string) => new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'long' });
const prayedLabel = (n: number) => (n === 0 ? 'Nadie ha orado todavía' : n === 1 ? '1 persona oró' : `${n} personas oraron`);

/** Oración (Motor 15). Las peticiones privadas solo las ve el equipo pastoral. */
export function PrayerPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canCare = me.can('prayer.care');
  const canRequest = me.can('prayer.request');
  const [tab, setTab] = useTabParam<Tab>(['church', 'mine', 'pastoral', 'team', 'jornadas']);
  const overview = useAsync(
    (signal) => (churchId ? api.get<PrayerOverview>(`/prayer/churches/${churchId}/sessions`, signal) : Promise.resolve(null)),
    [churchId],
  );
  const team = overview.status === 'ready' && overview.data ? overview.data.team : { member: false, leader: false };
  const list = useAsync(
    (signal) => (churchId && tab !== 'jornadas' ? api.get<{ items: PrayerRequest[] }>(`/prayer/churches/${churchId}/requests?list=${tab}`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId, tab],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  const tabs: Tab[] = [...(canCare ? (['church', 'mine', 'pastoral'] as Tab[]) : canRequest ? (['church', 'mine'] as Tab[]) : (['church'] as Tab[])), ...(team.member ? (['team'] as Tab[]) : []), 'jornadas'];

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Oración</h1>
        <p className="muted">
          Oremos unos por otros. <Link to="/app/oracion/diario">Mi diario de oración</Link>
        </p>
      </header>

      {canRequest ? <NewRequestCard churchId={churchId} onCreated={() => (tab === 'mine' ? list.reload() : setTab('mine'))} /> : null}

      <div className="tabs tabs--scroll" role="tablist" aria-label="Peticiones">
        {tabs.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className={`tabs__tab${tab === t ? ' tabs__tab--active' : ''}`} onClick={() => setTab(t)}>
            {TABS[t]}
          </button>
        ))}
      </div>

      {tab === 'jornadas' ? (
        <>
          {overview.status === 'ready' && overview.data ? <PrayerDaysCard overview={overview.data} /> : null}
          <PrayerChainsCard />
        </>
      ) : null}
      {tab === 'pastoral' ? <Alert tone="info">Peticiones privadas. Tu acceso queda registrado; trátalas con confidencialidad.</Alert> : null}
      {tab !== 'jornadas' && list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {tab !== 'jornadas' && list.status === 'ready' && list.data.length === 0 ? <EmptyState title={tab === 'mine' ? 'Aún no has pedido oración.' : 'No hay peticiones abiertas.'} /> : null}
      {tab === 'team' && list.status === 'ready' && list.data.length > 0 ? <TeamShare requests={list.data} /> : null}
      {list.status === 'ready' ? list.data.map((r) => <RequestCard key={r.id} churchId={churchId} request={r} onChange={list.reload} />) : null}
    </div>
  );
}

function RequestCard({ churchId, request: r, onChange }: { churchId: string; request: PrayerRequest; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const base = `/prayer/churches/${churchId}/requests/${r.id}`;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setAnswering(false);
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function onAnswered(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const note = String(new FormData(event.currentTarget).get('note') || '') || null;
    void run(() => api.post(`${base}/answered`, { note }));
  }

  return (
    <Card>
      <p className="prayer__body">{r.body}</p>
      <p className="muted small">
        {r.author ? r.author.name : 'Anónima'} · {when(r.createdAt)}
        {r.visibility === 'pastoral' ? ' · Solo equipo pastoral' : r.visibility === 'ministry' ? ' · Para el ministerio de Oración' : ''}
        {r.isMine && r.anonymous ? ' · Compartida sin tu nombre' : ''}
      </p>
      {r.status !== 'open' ? (
        <p className="small">
          <span className="badge badge--active">{STATUS[r.status]}</span> {r.answerNote}
        </p>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
      {r.status === 'open' ? (
        <div className="actions prayer__actions">
          <span className="muted small">{prayedLabel(r.prayedCount)}</span>
          {r.prayedToday ? (
            <span className="badge badge--active">Oraste hoy</span>
          ) : (
            <Button variant="secondary" busy={busy} onClick={() => run(() => api.post(`${base}/pray`))}>
              Oré por esto
            </Button>
          )}
          {r.isMine && !answering ? (
            <Button variant="ghost" onClick={() => setAnswering(true)}>
              Fue respondida
            </Button>
          ) : null}
        </div>
      ) : null}
      {answering ? (
        <form className="form" onSubmit={onAnswered} noValidate>
          <Field label="Cuéntanos (opcional)" name="note" maxLength={1000} />
          <div className="actions">
            <Button type="submit" busy={busy}>
              Marcar respondida
            </Button>
            <Button type="button" variant="ghost" busy={busy} onClick={() => run(() => api.post(`${base}/close`))}>
              Solo cerrarla
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

function NewRequestCard({ churchId, onCreated }: { churchId: string; onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<'church' | 'pastoral' | 'ministry'>('church');

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.post(`/prayer/churches/${churchId}/requests`, {
        body: String(form.get('body')),
        visibility,
        anonymous: visibility === 'church' && form.get('anonymous') === 'on',
      });
      setOpen(false);
      onCreated();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="actions">
        <Button onClick={() => setOpen(true)}>Pedir oración</Button>
      </div>
    );
  }

  return (
    <Card title="Pedir oración">
      <form className="form" onSubmit={onSubmit} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <div className="field">
          <label htmlFor="prayer-body">¿Por qué quieres que oremos?</label>
          <textarea id="prayer-body" name="body" required minLength={3} maxLength={2000} rows={3} />
        </div>
        <fieldset className="choices">
          <legend>¿Quién la verá?</legend>
          <label className="choice">
            <input type="radio" name="visibility" checked={visibility === 'church'} onChange={() => setVisibility('church')} />
            <span>La iglesia</span>
          </label>
          <label className="choice">
            <input type="radio" name="visibility" checked={visibility === 'ministry'} onChange={() => setVisibility('ministry')} />
            <span>El ministerio de Oración</span>
          </label>
          <label className="choice">
            <input type="radio" name="visibility" checked={visibility === 'pastoral'} onChange={() => setVisibility('pastoral')} />
            <span>Solo el equipo pastoral</span>
          </label>
        </fieldset>
        {visibility === 'church' ? (
          <label className="choice">
            <input type="checkbox" name="anonymous" />
            <span>Compartir sin mi nombre</span>
          </label>
        ) : null}
        <div className="actions">
          <Button type="submit" busy={busy}>
            Enviar petición
          </Button>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Próximas jornadas de oración (las ve toda la iglesia). */
function PrayerDaysCard({ overview }: { overview: PrayerOverview }) {
  const upcoming = overview.items.filter((d) => !d.canceled);
  if (upcoming.length === 0 && !overview.team.leader) return null;
  return (
    <Card
      title="Jornadas de oración"
      action={
        overview.team.leader ? (
          <Link className="btn btn--primary" to="/app/oracion/jornadas/nueva">
            Nueva jornada
          </Link>
        ) : null
      }
    >
      {upcoming.length === 0 ? <p className="muted small">No hay jornadas programadas.</p> : null}
      <ul className="list">
        {upcoming.map((d) => (
          <li key={d.id} className="list__item">
            <span className="agenda__body">
              <Link to={`/app/oracion/jornadas/${d.id}`}>
                <strong>{dayTitle(d)}</strong>
              </Link>
              <span className="muted small">{formatWhen(d.startsAt, d.endsAt)}</span>
              <span className="small">
                {d.mode === 'virtual' ? 'Virtual' : `Presencial${d.placeName ? ` · ${d.placeName}` : ''}`}
                {d.leader ? ` · Dirige ${d.leader.name}` : ''}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** El equipo elige peticiones y las exporta a PDF o las manda por WhatsApp. */
function TeamShare({ requests }: { requests: PrayerRequest[] }) {
  const [selected, setSelected] = useState<string[]>([]);
  const chosen = requests.filter((r) => selected.includes(r.id));
  return (
    <Card title="Compartir peticiones">
      <p className="small muted">Elige las peticiones y expórtalas a PDF o mándalas por WhatsApp.</p>
      <ul className="picker__list">
        {requests.map((r) => (
          <li key={r.id}>
            <label className="check" htmlFor={`sel-${r.id}`}>
              <input id={`sel-${r.id}`} type="checkbox" checked={selected.includes(r.id)} onChange={() => setSelected((s) => (s.includes(r.id) ? s.filter((x) => x !== r.id) : [...s, r.id]))} />
              <span>{r.body.length > 90 ? `${r.body.slice(0, 89)}…` : r.body}</span>
            </label>
          </li>
        ))}
      </ul>
      <div className="actions">
        <Button variant="ghost" onClick={() => setSelected(selected.length === requests.length ? [] : requests.map((r) => r.id))}>
          {selected.length === requests.length ? 'Quitar selección' : 'Seleccionar todas'}
        </Button>
        {chosen.length > 0 ? (
          <>
            <Link className="btn btn--secondary" to={printUrl({ ids: selected })} target="_blank">
              Exportar PDF ({chosen.length})
            </Link>
            <a className="btn btn--primary" href={whatsappUrl(whatsappText(chosen, 'Peticiones de oración'))} target="_blank" rel="noopener noreferrer">
              Enviar por WhatsApp
            </a>
          </>
        ) : null}
      </div>
    </Card>
  );
}
